import endpoints from 'constants/endpoints';
import { stateAbbreviations } from 'constants/enum';
import { safeAWSCallPost, safePostCall } from 'helpers/firebaseSetup';
import { parseZohoOkMessageOutput } from 'helpers/zoho/response-parsers';
import store from 'state/store/store';
import { showAlert } from 'state/actions/site';

const abbrToFullStateName = Object.fromEntries(
  Object.entries(stateAbbreviations).map(([name, abbr]) => [abbr, name])
);

export const RECOMMENDATION_TYPES = {
  NOT_APPOINTED_POLICIES: 'not_appointed_policies',
  CARRIER_MISSING_APPOINTMENTS: 'carrier_missing_appointments',
  TOP_SELLER_MISSING_APPOINTMENTS: 'top_seller_missing_appointments',
  UNTAPPED_AGENT: 'untapped_agent'
};

function pickId(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return String(v);
  }
  return undefined;
}

function pickValue(obj, keys) {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (v !== undefined && v !== null && v !== '') return v;
  }
  return undefined;
}

function toStateFullName(state) {
  const value = String(state || '').trim();
  if (!value) return '';
  if (value.length === 2) {
    return abbrToFullStateName[value.toUpperCase()] || value;
  }
  if (Object.prototype.hasOwnProperty.call(stateAbbreviations, value)) {
    return value;
  }
  return value;
}

/** Full state names for Zoho email, comma-separated, e.g. "Florida, Ohio, Texas". */
export function formatRecommendationStatesForEmail(states) {
  if (!Array.isArray(states)) return '';
  const names = states.map(toStateFullName).filter(Boolean);
  return names.join(', ');
}

/** Untapped agents from `get_agents_w_appt_info` use lowercase `email`. */
export function getUntappedAgentEmail(agentRecord) {
  const raw = pickValue(agentRecord, [
    'email',
    'Email',
    'agentEmail',
    'agent_email'
  ]);
  return raw != null ? String(raw).trim().toLowerCase() : '';
}

/** Agent contact fields returned by recommendation APIs. */
export function agentRecommendationContact(record) {
  const agentName =
    pickValue(record, ['agentName', 'agent_name', 'Full_Name']) ?? '';
  const fromUntapped = getUntappedAgentEmail(record);
  const agentEmail =
    fromUntapped ||
    (pickValue(record, ['agentEmail', 'agent_email', 'Email', 'email']) != null
      ? String(
          pickValue(record, ['agentEmail', 'agent_email', 'Email', 'email'])
        )
          .trim()
          .toLowerCase()
      : '');
  return {
    agentName,
    agentEmail,
    agentNpn: pickValue(record, ['agentNpn', 'agent_npn', 'npn', 'NPN']) ?? ''
  };
}

/**
 * Untapped bulk email — `{ carrierName, states }[]` for Zoho Deluge loop.
 * `states` is comma-separated full names or "" when carrier has no state filter.
 */
export function formatUntappedCarriersForEmail(availableCarriers) {
  if (!Array.isArray(availableCarriers)) return [];
  return availableCarriers.map(carrier => {
    const statesList = carrier?.states ?? [];
    return {
      carrierName: String(carrier?.name ?? carrier?.carrierName ?? '').trim(),
      states:
        statesList.length > 0
          ? formatRecommendationStatesForEmail(statesList)
          : ''
    };
  });
}

function normalizeCarriersForEmailPayload({ carriers, carrierName, state }) {
  if (Array.isArray(carriers) && carriers.length > 0) {
    return carriers.map(c => ({
      carrierName: String(c?.carrierName ?? '').trim(),
      states: c?.states != null ? String(c.states).trim() : ''
    }));
  }
  return [
    {
      carrierName: String(carrierName || '').trim(),
      states:
        state != null && String(state).trim() !== '' ? String(state).trim() : ''
    }
  ];
}

/**
 * Sends the agency recommendation email via Zoho (Suggest to agent).
 * @see agency_recommendations_email
 */
export function sendAgencyRecommendationEmail({
  agentName,
  mainZohoId,
  agencyName,
  carriers,
  carrierName,
  state = '',
  npn,
  agentEmail,
  useLowercaseEmailKey = false,
  showSuccessAlert = true,
  showErrorAlert = true
}) {
  const email = String(agentEmail || '')
    .trim()
    .toLowerCase();
  if (!email) {
    store.dispatch(
      showAlert({
        message: 'Unable to send email — agent email is missing.',
        code: 'error'
      })
    );
    return Promise.resolve(false);
  }

  const carriersPayload = normalizeCarriersForEmailPayload({
    carriers,
    carrierName,
    state
  });

  const body = {
    isZohoRequest: true,
    endpoint: endpoints.zoho.agencyRecommendationsEmail,
    agentName: String(agentName || '').trim(),
    adminId: String(mainZohoId || '').trim(),
    agencyName: String(agencyName || '').trim(),
    carriers: carriersPayload,
    npn: npn == null ? '' : String(npn).trim(),
    ...(useLowercaseEmailKey ? { email } : { agentEmail: email })
  };

  return safePostCall(endpoints.awsProxy.safePost, body)
    .then(response => {
      if (!response?.data?.details) {
        if (showErrorAlert) {
          store.dispatch(
            showAlert({
              message:
                'Something went wrong sending the email. Try again later.',
              code: 'error'
            })
          );
        }
        return false;
      }

      const { ok, message } = parseZohoOkMessageOutput(
        response.data.details.output
      );

      if (ok && showSuccessAlert) {
        store.dispatch(
          showAlert({
            message: 'Suggestion sent successfully to agent.',
            code: 'success'
          })
        );
      }
      if (!ok && showErrorAlert) {
        store.dispatch(
          showAlert({
            message:
              message ||
              'Something went wrong sending the email. Try again later.',
            code: 'error'
          })
        );
      }
      return ok;
    })
    .catch(err => {
      console.warn(
        '[agency recommendations] failed to send recommendation email',
        err
      );
      if (showErrorAlert) {
        store.dispatch(
          showAlert({
            message: 'Something went wrong sending the email. Try again later.',
            code: 'error'
          })
        );
      }
      return false;
    });
}

async function postRecommended(body) {
  try {
    const response = await safeAWSCallPost(
      endpoints.aws.saveRecommendedRecommendation,
      body
    );
    return response?.data ?? null;
  } catch (err) {
    console.warn(
      '[agency recommendations] failed to save viewed recommendation',
      err
    );
    return null;
  }
}

/**
 * Marks a not-appointed policy recommendation as viewed/suggested.
 * Call only when the user dismisses or confirms via "Suggest to agent".
 */
export function saveNotAppointedPolicyRecommendation({
  agencyId,
  policyId,
  agentId,
  carrierId,
  state,
  metadata
}) {
  if (!agencyId || !policyId || !agentId) return Promise.resolve(null);
  const body = {
    agencyId,
    recommendationType: RECOMMENDATION_TYPES.NOT_APPOINTED_POLICIES,
    policyId,
    agentId,
    ...(metadata ? { metadata } : {})
  };
  if (carrierId) body.carrierId = carrierId;
  const stateStr = state != null ? String(state).trim() : '';
  if (stateStr) body.state = stateStr;
  return postRecommended(body);
}

function saveMissingAppointmentsRecommendation({
  agencyId,
  carrierId,
  entries,
  recommendationType
}) {
  if (
    !agencyId ||
    !carrierId ||
    !Array.isArray(entries) ||
    entries.length === 0
  ) {
    return Promise.resolve(null);
  }

  const valid = entries
    .map(e => ({
      carrierId,
      agentId: e?.agentId,
      state: e?.state ? String(e.state).trim() : ''
    }))
    .filter(e => e.agentId);

  if (valid.length === 0) return Promise.resolve(null);

  if (valid.length === 1) {
    const [entry] = valid;
    const body = {
      agencyId,
      recommendationType,
      carrierId: entry.carrierId,
      agentId: entry.agentId
    };
    if (entry.state) body.state = entry.state;
    return postRecommended(body);
  }

  return postRecommended({
    agencyId,
    recommendationType,
    items: valid.map(({ carrierId: cId, agentId, state }) => {
      const item = { carrierId: cId, agentId };
      if (state) item.state = state;
      return item;
    })
  });
}

/**
 * AWS save rows for untapped bulk suggest (one row per carrier or per state).
 */
export function buildUntappedRecommendationSaveItems({
  agentId,
  availableCarriers
}) {
  if (!agentId || !Array.isArray(availableCarriers)) return [];

  const items = [];
  for (const carrier of availableCarriers) {
    const carrierId =
      carrier?.id != null && String(carrier.id).trim() !== ''
        ? String(carrier.id)
        : '';
    if (!carrierId) continue;

    const states = carrier?.states ?? [];
    if (states.length === 0) {
      items.push({ carrierId, agentId: String(agentId), state: '' });
    } else {
      for (const state of states) {
        const stateStr = String(state).trim();
        if (!stateStr) continue;
        items.push({
          carrierId,
          agentId: String(agentId),
          state: stateStr
        });
      }
    }
  }
  return items;
}

/**
 * Untapped tab — untapped_agent after successful Zoho email.
 */
export function saveUntappedCarrierMissingAppointmentsRecommendations({
  agencyId,
  agentId,
  availableCarriers
}) {
  const items = buildUntappedRecommendationSaveItems({
    agentId,
    availableCarriers
  });

  if (!agencyId || items.length === 0) return Promise.resolve(null);

  const recommendationType = RECOMMENDATION_TYPES.UNTAPPED_AGENT;

  if (items.length === 1) {
    const [entry] = items;
    const body = {
      agencyId,
      recommendationType,
      carrierId: entry.carrierId,
      agentId: entry.agentId
    };
    if (entry.state) body.state = entry.state;
    return postRecommended(body);
  }

  return postRecommended({
    agencyId,
    recommendationType,
    items: items.map(({ carrierId, agentId, state }) => {
      const item = { carrierId, agentId };
      if (state) item.state = state;
      return item;
    })
  });
}

/**
 * Sends untapped recommendations email (multi-carrier) and saves tracking.
 */
export async function suggestUntappedAgentRecommendations({
  agencyId,
  agencyName,
  mainZohoId,
  agentId,
  agentName,
  email,
  npn = '',
  availableCarriers,
  showSuccessAlert = true,
  showErrorAlert = true
}) {
  const agentEmail = String(email ?? '')
    .trim()
    .toLowerCase();
  const displayName = String(agentName || '').trim() || String(agentId);

  const carriers = formatUntappedCarriersForEmail(availableCarriers);
  if (carriers.length === 0) {
    if (showErrorAlert) {
      store.dispatch(
        showAlert({
          message: 'No carrier recommendations to send.',
          code: 'error'
        })
      );
    }
    return false;
  }

  const sent = await sendAgencyRecommendationEmail({
    agentName: displayName,
    mainZohoId,
    agencyName,
    carriers,
    npn: npn == null ? '' : String(npn).trim(),
    agentEmail,
    useLowercaseEmailKey: true,
    showSuccessAlert,
    showErrorAlert
  });

  if (!sent) return false;

  const saveItems = buildUntappedRecommendationSaveItems({
    agentId,
    availableCarriers
  });

  if (agencyId && agentId && saveItems.length > 0) {
    const saveResult =
      await saveUntappedCarrierMissingAppointmentsRecommendations({
        agencyId,
        agentId,
        availableCarriers
      });

    if (!saveResult) {
      if (showErrorAlert) {
        store.dispatch(
          showAlert({
            message:
              'Email was sent but saving the suggestion failed. Try again later.',
            code: 'error'
          })
        );
      }
      return false;
    }
  }

  return true;
}

/**
 * Featured carrier modal — carrier_missing_appointments.
 */
export function saveCarrierMissingAppointmentsRecommendation({
  agencyId,
  carrierId,
  entries
}) {
  return saveMissingAppointmentsRecommendation({
    agencyId,
    carrierId,
    entries,
    recommendationType: RECOMMENDATION_TYPES.CARRIER_MISSING_APPOINTMENTS
  });
}

/**
 * Coverage gap cards — top_seller_missing_appointments.
 */
export function saveTopSellerMissingAppointmentsRecommendation({
  agencyId,
  carrierId,
  entries
}) {
  return saveMissingAppointmentsRecommendation({
    agencyId,
    carrierId,
    entries,
    recommendationType: RECOMMENDATION_TYPES.TOP_SELLER_MISSING_APPOINTMENTS
  });
}

/** Extract ids from a policy row returned by not-appointed-policies. */
export function policyRecommendationIds(policy) {
  return {
    policyId: pickId(policy, ['policyId', 'policy_id', 'id']),
    agentId: pickId(policy, ['agentId', 'agent_id']),
    carrierId: pickId(policy, ['carrierId', 'carrier_id']),
    state: policy?.state ? String(policy.state).trim() : ''
  };
}

/** Stable key for dismissing a policy card from the carousel. */
export function policyRecommendationKey(policy) {
  const { policyId, agentId } = policyRecommendationIds(policy);
  if (policyId) return `policy:${policyId}`;
  return `policy:${agentId ?? ''}-${policy?.carrierId ?? ''}-${
    policy?.createdAt ?? ''
  }`;
}

/** Extract ids from a flattened coverage-gap row. */
export function coverageGapRecommendationIds(gap) {
  return {
    carrierId: pickId(gap, ['carrierId', 'carrier_id', 'id']),
    agentId: pickId(gap, ['agentId', 'agent_id']),
    state: gap?.state ? String(gap.state).trim() : ''
  };
}

/** Stable key for dismissing a coverage-gap card from the carousel. */
export function coverageGapRecommendationKey(gap) {
  const { agentId, carrierId, state } = coverageGapRecommendationIds(gap);
  return `gap:${agentId ?? ''}::${state}::${
    carrierId ?? gap?.carrierName ?? ''
  }`;
}
